// @flow

import React from 'react';
import * as Yup from 'yup';
import * as Actions from 'main-app/store/Actions';
import { i18n } from 'shared/utils';
import AddWarehouseLocationMutation from 'main-app/mutations/AddWarehouseLocation';
import UpdateWarehouseLocationMutation from 'main-app/mutations/UpdateWarehouseLocation';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import { Formik, Form, FieldGroup } from 'shared/components/form';
import Button from 'shared/components/common/Button';

type Props = {
  onClose: () => void,
  onSuccess?: (location: Object) => void,
  location: ?{
    id: string,
    name: string,
    code: ?string,
    description: ?string,
    isActive: boolean,
  },
};

const LANGUAGE = {
  update: {
    title: i18n.t('Edit Warehouse Location'),
    submitButton: i18n.t('Update Location'),
    successMessage: i18n.t('Warehouse Location Successfully Updated'),
  },
  add: {
    title: i18n.t('Create Warehouse Location'),
    submitButton: i18n.t('Create Location'),
    successMessage: i18n.t('Warehouse Location Successfully Created'),
  },
};

const AddUpdateWarehouseLocationModal = ({
  onClose,
  onSuccess,
  location,
}: Props) => {
  const language = LANGUAGE[location ? 'update' : 'add'];

  return (
    <Modal maxWidth={700}>
      <Formik
        initialValues={{
          name: location?.name || '',
          code: location?.code || '',
          description: location?.description || '',
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required('Required'),
          code: Yup.string(),
          description: Yup.string(),
        })}
        onSubmit={async (values: *, { setSubmitting }: *) => {
          setSubmitting(true);

          const mutation = location
            ? UpdateWarehouseLocationMutation
            : AddWarehouseLocationMutation;

          const variables: any = {
            input: {
              name: values.name,
              code: values.code || null,
              description: values.description || null,
            },
          };

          if (location) {
            variables.input.id = location.id;
          }

          try {
            const result = await mutation.commit({ variables });

            onClose();

            Actions.alertNotification(language.successMessage, 'success');

            if (onSuccess) {
              const edge = location
                ? result.updateWarehouseLocation.warehouseLocationEdge
                : result.addWarehouseLocation.warehouseLocationEdge;
              onSuccess(edge.node);
            }
          } catch (e) {
            setSubmitting(false);
            Actions.alertNotification(e.message, 'error');
          }
        }}
        render={({ errors, isValid, isSubmitting, handleSubmit }) => (
          <Form>
            <ModalHeader header={language.title} onClose={onClose} />
            <ModalBody withPadding>
              <FieldGroup
                name="name"
                label={i18n.t('Location Name')}
                placeholder={i18n.t('e.g. Warehouse A, Shelf B-3')}
                error={errors.name}
              />
              <FieldGroup
                name="code"
                label={i18n.t('Location Code')}
                placeholder={i18n.t('e.g. WH-A, SH-B3')}
                error={errors.code}
              />
              <FieldGroup
                name="description"
                label={i18n.t('Description')}
                placeholder={i18n.t('Optional description')}
                error={errors.description}
                type="textarea"
              />
            </ModalBody>
            <ModalFooter>
              <Button
                type="submit"
                theme="blue"
                disabled={!isValid}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {language.submitButton}
              </Button>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

export default AddUpdateWarehouseLocationModal;
